import React, { useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Alert,
  LinearProgress,
  Card,
  CardContent
} from '@mui/material'
import { CloudUpload, CheckCircle } from '@mui/icons-material'

const POLL_INTERVAL_MS = 2000 // delay between status polls
const MAX_FILE_BYTES = 50 * 1024 * 1024 // 50MB — mirrors backend cap (defense-in-depth)
const ALLOWED_EXT = ['pdf', 'docx']

/**
 * Upload a BRD → enqueue an async drafting job → poll until Ready/Failed.
 *
 * Step is DERIVED from Redux state (no local step machine):
 *   no jobId        → 'upload'
 *   jobId, no result→ 'polling'
 *   result          → 'result'
 * Polling is driven by `draftPollAttempts`: each still-processing poll bumps it,
 * which re-runs the effect and schedules the next tick. SUCCESS/FAILURE both stop
 * the loop (draftResult / pollDraftJobError gate the effect).
 */
export function DraftUploadDialog({
  open,
  onClose,
  onFileSelect,
  onUpload,
  onPoll,
  onReset,
  onUseDraft,
  draftFile,
  uploadDocumentLoader,
  uploadDocumentError,
  pollDraftJobError,
  draftJobId,
  draftPollAttempts,
  draftResult
}) {
  const step = draftResult ? 'result' : draftJobId ? 'polling' : 'upload'

  // Clear any state from a previous run whenever the dialog (re)opens, so a
  // prior document's draft can never leak into a fresh session.
  useEffect(() => {
    if (open) {
      onReset()
    }
  }, [open, onReset])

  // Poll loop: schedule the next status check while a job is in flight and not
  // yet resolved. Re-arms on draftPollAttempts (bumped by each still-processing
  // poll). Halts once a result or an error arrives.
  useEffect(() => {
    if (!open || !draftJobId || draftResult || pollDraftJobError) {
      return undefined
    }
    const timer = setTimeout(() => onPoll(draftJobId), POLL_INTERVAL_MS)
    return () => clearTimeout(timer)
  }, [
    open,
    draftJobId,
    draftPollAttempts,
    draftResult,
    pollDraftJobError,
    onPoll
  ])

  const validateFile = file => {
    const ext = file.name
      .split('.')
      .pop()
      .toLowerCase()
    if (!ALLOWED_EXT.includes(ext)) {
      return `Unsupported file type ".${ext}". Upload a PDF or DOCX.`
    }
    if (file.size > MAX_FILE_BYTES) {
      return 'File exceeds the 50MB limit.'
    }
    return null
  }

  const handleFileInputChange = e => {
    const file = e.target.files && e.target.files[0]
    if (file) {
      onFileSelect(file)
    }
  }

  const handleUpload = () => {
    if (draftFile && !validateFile(draftFile)) {
      onUpload(draftFile)
    }
  }

  const fileError = draftFile ? validateFile(draftFile) : null

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Generate Product from Document</DialogTitle>
      <DialogContent sx={{ minHeight: 400 }}>
        {step === 'upload' && (
          <Box>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Upload a product document (PDF or DOCX) to auto-generate the
              product definition.
            </Typography>

            {uploadDocumentError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {uploadDocumentError}
              </Alert>
            )}

            {fileError && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {fileError}
              </Alert>
            )}

            <Card sx={{ mb: 3, p: 2, textAlign: 'center' }}>
              <input
                type="file"
                accept=".pdf,.docx"
                onChange={handleFileInputChange}
                style={{ display: 'none' }}
                id="draft-file-input"
              />
              <label
                htmlFor="draft-file-input"
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  cursor: 'pointer'
                }}
              >
                <CloudUpload sx={{ fontSize: 48, color: '#173E4B', mb: 1 }} />
                <Typography variant="h6">
                  {draftFile ? draftFile.name : 'Click to select file'}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  PDF or DOCX (max 50MB)
                </Typography>
              </label>
            </Card>

            {draftFile && (
              <Card sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="subtitle2">Selected File</Typography>
                  <Typography variant="body2">
                    Name: {draftFile.name}
                  </Typography>
                  <Typography variant="body2">
                    Size: {(draftFile.size / 1024 / 1024).toFixed(2)} MB
                  </Typography>
                </CardContent>
              </Card>
            )}
          </Box>
        )}

        {step === 'polling' && (
          <Box sx={{ textAlign: 'center' }}>
            {pollDraftJobError ? (
              <Alert severity="error" sx={{ mb: 2 }}>
                {pollDraftJobError}
              </Alert>
            ) : (
              <>
                <CircularProgress sx={{ mb: 2 }} />
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Processing Document
                </Typography>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{ mb: 2 }}
                >
                  Rendering and analyzing document…
                </Typography>
                <LinearProgress variant="indeterminate" sx={{ mb: 2 }} />
                <Typography variant="caption">
                  This may take a few minutes depending on document size.
                </Typography>
              </>
            )}
          </Box>
        )}

        {step === 'result' && draftResult && (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <CheckCircle sx={{ color: 'green', mr: 1 }} />
              <Typography variant="h6">Draft Generated</Typography>
            </Box>

            {draftResult.draft_product && (
              <Card sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="subtitle2">
                    Product Definition
                  </Typography>
                  <pre
                    style={{
                      fontSize: '0.75rem',
                      overflow: 'auto',
                      maxHeight: 300
                    }}
                  >
                    {JSON.stringify(draftResult.draft_product, null, 2)}
                  </pre>
                </CardContent>
              </Card>
            )}

            {typeof draftResult.confidence === 'number' && (
              <Typography variant="body2">
                Confidence: {(draftResult.confidence * 100).toFixed(1)}%
              </Typography>
            )}

            {draftResult.open_questions &&
              draftResult.open_questions.length > 0 && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  <Typography variant="subtitle2">Open Questions</Typography>
                  {draftResult.open_questions.map(q => (
                    <Typography key={q} variant="body2">
                      • {q}
                    </Typography>
                  ))}
                </Alert>
              )}

            {draftResult.excluded && draftResult.excluded.length > 0 && (
              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="subtitle2">Excluded Fields</Typography>
                {draftResult.excluded.map(f => (
                  <Typography key={f} variant="body2">
                    • {f}
                  </Typography>
                ))}
              </Alert>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>
          {step === 'result' ? 'Close' : 'Cancel'}
        </Button>
        {step === 'upload' && draftFile && (
          <Button
            onClick={handleUpload}
            disabled={uploadDocumentLoader || !!fileError}
            variant="contained"
          >
            {uploadDocumentLoader ? (
              <CircularProgress size={24} />
            ) : (
              'Upload & Generate'
            )}
          </Button>
        )}
        {step === 'result' &&
          draftResult &&
          draftResult.draft_product &&
          onUseDraft && (
            <Button
              onClick={() => onUseDraft(draftResult.draft_product)}
              variant="contained"
              color="success"
            >
              Use Draft → Create Product
            </Button>
          )}
      </DialogActions>
    </Dialog>
  )
}
